import React, { useState, useCallback, useMemo, useRef, useEffect, forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import styles from './Tooltip.module.css';

// ── CVA ───────────────────────────────────────────────────────────────────

const tooltip = cva(styles.base);

// ── Types ─────────────────────────────────────────────────────────────────

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

type Trigger = 'hover' | 'focus' | 'click';

interface TooltipProps {
  /** Tooltip content */
  title: React.ReactNode;
  /** Placement relative to trigger */
  placement?: Placement;
  /** How to trigger the tooltip */
  trigger?: Trigger | Trigger[];
  /** Controlled open state */
  open?: boolean;
  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Custom background color */
  color?: string;
  /** Show arrow pointing to trigger */
  arrow?: boolean;
  /** Delay before showing (ms) */
  mouseEnterDelay?: number;
  /** Delay before hiding (ms) */
  mouseLeaveDelay?: number;
  /** When true, suppress tooltip display */
  disabled?: boolean;
  /** Click handler passed through to the trigger child (e.g. from Popover wrapping Tooltip) */
  onClick?: (e: React.MouseEvent) => void;
  /** Trigger element */
  children: React.ReactElement;
}

// ── Arrow class map ───────────────────────────────────────────────────────

function getArrowClass(placement: Placement): string {
  const side = placement.split('-')[0];
  switch (side) {
    case 'top': return styles.arrowTop;
    case 'bottom': return styles.arrowBottom;
    case 'left': return styles.arrowLeft;
    case 'right': return styles.arrowRight;
    default: return styles.arrowTop;
  }
}

/**
 * Tooltip — hover/focus/click popup with short text content.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | title | ReactNode | — | Tooltip content |
 * | placement | Placement | 'top' | Position relative to trigger |
 * | trigger | 'hover' \| 'focus' \| 'click' \| array | 'hover' | How to trigger |
 * | open | boolean | — | Controlled open state |
 * | onOpenChange | (open) => void | — | Open state callback |
 * | color | string | — | Custom background |
 * | arrow | boolean | true | Show arrow |
 * | mouseEnterDelay | number | 100 | Show delay (ms) |
 * | mouseLeaveDelay | number | 100 | Hide delay (ms) |
 *
 * @example
 * <Tooltip title="Save your work">
 *   <Button>Save</Button>
 * </Tooltip>
 *
 * <Tooltip title="Click to copy" trigger="click" placement="right">
 *   <Text code copyable>npm install</Text>
 * </Tooltip>
 */
export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip({
  title,
  placement = 'top',
  trigger = ['hover', 'focus'],
  open: controlledOpen,
  onOpenChange,
  color,
  arrow = true,
  mouseEnterDelay = 100,
  mouseLeaveDelay = 100,
  disabled = false,
  onClick: onClickProp,
  children,
}: TooltipProps, forwardedRef) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = !disabled && (isControlled ? controlledOpen : internalOpen);

  const tooltipId = useRef(`tooltip-${Math.random().toString(36).slice(2, 9)}`).current;
  const enterTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { triggerRef, floatingRef, position } = useFloating<HTMLElement, HTMLDivElement>({
    placement,
    offset: arrow ? 10 : 6,
    open: isOpen,
  });

  const triggers = useMemo(() => (Array.isArray(trigger) ? trigger : [trigger]), [trigger]);

  const show = useCallback(() => {
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      if (!isControlled) setInternalOpen(true);
      onOpenChange?.(true);
    }, mouseEnterDelay);
  }, [isControlled, onOpenChange, mouseEnterDelay]);

  const hide = useCallback(() => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => {
      if (!isControlled) setInternalOpen(false);
      onOpenChange?.(false);
    }, mouseLeaveDelay);
  }, [isControlled, onOpenChange, mouseLeaveDelay]);

  const toggle = useCallback(() => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }, [isOpen, isControlled, onOpenChange]);

  // Close on outside click for click trigger
  useEffect(() => {
    if (!isOpen || !triggers.includes('click')) return;
    const handleClick = (e: MouseEvent) => {
      const trigger = triggerRef.current;
      const floating = floatingRef.current;
      if (trigger?.contains(e.target as Node) || floating?.contains(e.target as Node)) return;
      hide();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, triggers, hide, triggerRef, floatingRef]);

  // Clean up timers
  useEffect(() => {
    return () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    };
  }, []);

  // Build trigger props — merge with child's existing handlers so we don't
  // break animations or other behaviour (e.g. IconButton hover opacity).
  const childProps = children.props as Record<string, unknown>;

  const childrenRef = useRef(children);
  childrenRef.current = children;
  const forwardedRefRef = useRef(forwardedRef);
  forwardedRefRef.current = forwardedRef;

  const mergedRef = useCallback(
    (node: HTMLElement | null) => {
      (triggerRef as React.MutableRefObject<HTMLElement | null>).current = node;
      const childRef = (childrenRef.current.props as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof childRef === 'function') childRef(node);
      else if (childRef) (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
      const fwd = forwardedRefRef.current;
      if (typeof fwd === 'function') fwd(node);
      else if (fwd) (fwd as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    [triggerRef],
  );

  const triggerProps: Record<string, unknown> = {
    ref: mergedRef,
    'aria-describedby': isOpen ? tooltipId : undefined,
  };

  if (triggers.includes('hover')) {
    triggerProps.onMouseEnter = (e: React.MouseEvent) => {
      show();
      (childProps.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
    };
    triggerProps.onMouseLeave = (e: React.MouseEvent) => {
      // Force-hide on mouse leave — even if element retains focus from a click.
      // This prevents sticky tooltips when a user clicks a button then moves away.
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
      leaveTimer.current = setTimeout(() => {
        if (!isControlled) setInternalOpen(false);
        onOpenChange?.(false);
      }, mouseLeaveDelay);
      (childProps.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
    };
  }
  if (triggers.includes('focus')) {
    triggerProps.onFocus = (e: React.FocusEvent) => {
      // Only show on keyboard focus — skip programmatic/mouse-driven focus
      // (e.g. dialogs moving focus on open) to prevent sticky tooltips.
      if (e.currentTarget instanceof Element && e.currentTarget.matches(':focus-visible')) {
        show();
      }
      (childProps.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
    };
    triggerProps.onBlur = (e: React.FocusEvent) => {
      hide();
      (childProps.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
    };
  }
  if (triggers.includes('click') || onClickProp) {
    triggerProps.onClick = (e: React.MouseEvent) => {
      if (triggers.includes('click')) toggle();
      onClickProp?.(e);
      (childProps.onClick as ((e: React.MouseEvent) => void) | undefined)?.(e);
    };
  }

  if (!title) return children;

  const bgColor = color ?? 'var(--surface-container-lowest)';

  return (
    <>
      {React.cloneElement(children, triggerProps)}
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <motion.div
              ref={floatingRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: duration.normal / 1000 }}
              className={tooltip()}
              style={{
                '--tooltip-top': `${position.top}px`,
                '--tooltip-left': `${position.left}px`,
                '--tooltip-bg': bgColor,
              } as React.CSSProperties}
              id={tooltipId}
              role="tooltip"
            >
              {title}
              {arrow && <div className={`${styles.arrow} ${getArrowClass(placement)}`} />}
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
});
